import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Box, Divider, Paper, styled, Tab, Theme } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useQuery } from '@apollo/client';
import { getUserByIdTemplate } from 'apollo/templates/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { permissions } from 'utils/permission';
import DentistSettingsHeader from 'components/dentist-directory/dentist-settings/dentist-settings-header/dentist-settings-header.component';
import DentistsDetails from 'components/dentist-directory/dentist-settings/dentist-details/dentist-details.component';
import OrderHistory from 'components/dentist-directory/dentist-settings/order-history/order-history.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';

const DentistSettings: FC = () => {
    const { t: translate } = useTranslation(['client-directory']);
    const currentUser = currentUserVar();
    const { tab, id } = useParams<{ tab: string; id: string }>();
    const [tabState, setTabState] = useState('dentist-details');
    const navigate = useNavigate();
    const location = useLocation();
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const { data: { userById: dentist } = {}, refetch } = useQuery(getUserByIdTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            userId: id,
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setTabState(tab || 'dentist-details');
    }, [tab]);

    const handleTabChange = (_: unknown, newTabState: string) => {
        const routeMap: { [key: string]: string } = {
            'dentist-details': 'dentist-details',
            'order-history': 'order-history',
        };
        if (tabState !== newTabState) {
            const url = `/client-directory/dentists/${id}/${routeMap[newTabState]}${location.search}`;
            navigate(url);
            setTabState(newTabState);
        }
    };

    const handleUserUpdated = () => {
        refetch();
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('common:CHANGES_SAVED'),
        });
    };

    return (
        <Fragment>
            {permissions.isLabTechnician(currentUser.type) ? (
                <Fragment>
                    {dentist ? (
                        <Paper variant="outlined" style={{ overflowX: 'auto' }}>
                            <DentistSettingsHeader dentist={dentist} />
                            <TabContext value={tabState}>
                                <Box display="flex" mb={2} ml={2}>
                                    <Box display="flex">
                                        <TabList
                                            sx={{ minWidth: 250 }}
                                            onChange={handleTabChange}
                                            indicatorColor="primary"
                                            textColor="primary"
                                            orientation="vertical"
                                        >
                                            <SettingsTab
                                                label={translate('client-directory:DENTIST_DETAILS')}
                                                value="dentist-details"
                                            />
                                            <SettingsTab
                                                label={translate('client-directory:ORDER_HISTORY')}
                                                value="order-history"
                                            />
                                        </TabList>
                                        <Box mx={3}>
                                            <Divider orientation="vertical" />
                                        </Box>
                                    </Box>
                                    <Box flexGrow={1} pr={2}>
                                        <TabPanel sx={{ padding: 0 }} value="dentist-details">
                                            <DentistsDetails dentist={dentist} onUserUpdated={handleUserUpdated} />
                                        </TabPanel>
                                        <TabPanel sx={{ padding: 0 }} value="order-history">
                                            <OrderHistory />
                                        </TabPanel>
                                    </Box>
                                </Box>
                            </TabContext>
                        </Paper>
                    ) : undefined}
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

interface SettingsTabProps {
    label: string;
    value: string;
    theme?: Theme;
}
const SettingsTab = styled((props: SettingsTabProps) => <Tab {...props} />)(({ theme }: { theme?: Theme }) => {
    return {
        color: theme?.palette.primary.main,
        maxWidth: 'none',
        '&.Mui-selected': {
            fontWeight: theme?.typography.fontWeightBold,
            background: theme?.palette.primary.light,
            color: '#fff',
        },
        '&:hover': {
            opacity: 0.8,
        },
        alignItems: 'start',
        textAlign: 'left',
    };
});
export default DentistSettings;
